import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { AddScholarship } from '../../classes/add-scholarship';
import { School } from '../../classes/school';
import { AppService } from '../../services/app.service';
import { UserInfo, UserService } from '../../services/user.service';
import { validateDecimal } from '../../validators/decimal.validator';
import { validateNumber } from '../../validators/number.validator';
import { EditSchoolsModalComponent } from '../modals/edit-schools-modal/edit-schools-modal.component';
import { validateZipCode } from '../../validators/zipcode.validator';
import { STATES } from '../../classes/states';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { ErrorService } from '../../services/error.service';

@Component({
    selector: 'app-add-scholarship',
    templateUrl: './add-scholarship.component.html',
    styleUrls: ['./add-scholarship.component.scss'],
    imports: [
        MatRadioModule,
        ReactiveFormsModule,
        MatInputModule,
        MatDividerModule,
        MatCardModule,
        MatButtonModule,
        MatFormFieldModule,
        MatSelectModule
    ]
})
export class AddScholarshipComponent implements OnInit {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly matDialog = inject(MatDialog);
    protected readonly router = inject(Router);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly userSvc = inject(UserService);
    protected readonly errorSvc = inject(ErrorService);
    user: UserInfo;
    scholarshipForm: FormGroup;
    allStates = STATES;
    allSchools: School[];
    selectedSchools: School[] = [];
    formSubmitted: boolean = false;
    loading: boolean = true;
    aQInvalid: boolean = false;

    ngOnInit() {
        this.user = this.userSvc.User;
        this.createForm();
        this.getSchools();
    }

    createForm() {
        this.scholarshipForm = new FormGroup({
            ScholarshipName: new FormControl('', [Validators.required, Validators.maxLength(200)],),
            Description: new FormControl('', [Validators.required, Validators.maxLength(3000)],),
            Active: new FormControl(true),
            MinGPA: new FormControl('', validateDecimal),
            MinSAT: new FormControl('', validateNumber),
            MinACT: new FormControl('', validateNumber),
            MinCLT: new FormControl('', validateNumber),
            MinCommunityService: new FormControl('', [Validators.required, validateNumber]),
            MinWorkHours: new FormControl('', validateNumber),
            Advertised: new FormControl(true),
            AdultSchoolEligible: new FormControl('', Validators.maxLength(100)),
            TechTrainingUsageAllowed: new FormControl('', Validators.maxLength(100)),
            HardCopyApp: new FormControl('', Validators.maxLength(200)),
            YearEstablished: new FormControl('', Validators.maxLength(100)),
            ScholarshipType: new FormControl('', Validators.maxLength(20)),
            EndowedTotal: new FormControl('', Validators.maxLength(20)),
            HowScholarshipPaid: new FormControl('', Validators.maxLength(300)),
            CommitmentFormReceived: new FormControl('', Validators.maxLength(20)),
            DonorTermsFormReceived: new FormControl('', Validators.maxLength(20)),
            NbrScholarshipsAwarded: new FormControl('', Validators.maxLength(100)),
            AmountAwarded: new FormControl('', Validators.maxLength(100)),
            TotalAmountAvailable: new FormControl('', Validators.maxLength(100)),
            ScholarshipDistribution: new FormControl('', Validators.maxLength(150)),
            ApplicantPool: new FormControl('', Validators.maxLength(500)),
            EssaySubject: new FormControl('', Validators.maxLength(1000)),
            RenewalEligibilitySpecialRequirements: new FormControl('', Validators.maxLength(500),),
            FinancialNeed: new FormControl('', Validators.maxLength(50)),
            StudentEmploymentNarrowing: new FormControl('', Validators.maxLength(200)),
            DonorSpecficNarrowing: new FormControl('', Validators.maxLength(1500)),
            RankingPriorities: new FormControl('', Validators.maxLength(250)),
            SelectionProcess: new FormControl('', Validators.maxLength(250)),
            InterviewsRequired: new FormControl('', Validators.maxLength(50)),
            NbrApplicantsToInterview: new FormControl('', Validators.maxLength(100)),
            InterviewScreeningNotes: new FormControl('', Validators.maxLength(250)),
            AwardLetterInfoToInclude: new FormControl('', Validators.maxLength(200)),
            SpecialNotes: new FormControl('', Validators.maxLength(1500)),
            TranscriptsNeededForRenewal: new FormControl('', Validators.maxLength(25)),
            DonorPresenting: new FormControl('', Validators.maxLength(150)),
            WhoWillPresent: new FormControl('', Validators.maxLength(150)),
            DonorName: new FormControl('', Validators.maxLength(200)),
            DonorAddress: new FormControl('', Validators.maxLength(60)),
            DonorCity: new FormControl('', Validators.maxLength(30)),
            DonorState: new FormControl('', Validators.maxLength(10)),
            DonorZip: new FormControl('', [validateZipCode]),
            DonorPhone: new FormControl('', Validators.maxLength(100)),
            DonorEmail: new FormControl('', Validators.maxLength(100)),
            AdditionalContact: new FormControl('', Validators.maxLength(150)),
            SecondaryAddress: new FormControl('', Validators.maxLength(60)),
            SecondaryCity: new FormControl('', Validators.maxLength(30)),
            SecondaryState: new FormControl('', Validators.maxLength(10)),
            SecondaryZip: new FormControl('', [validateZipCode]),
            SecondaryEmail: new FormControl('', Validators.maxLength(100)),
            SecondaryPhone: new FormControl('', Validators.maxLength(100)),
            AdditionalQuestion: new FormControl('', Validators.maxLength(1500)),
            AqHaveYesNo: new FormControl(false),
            AqYesRequired: new FormControl(false),
            AqHaveText: new FormControl(false),
            EmployeeName: new FormControl(false),
            EmployeeLocation: new FormControl(false),
            EmployeeSSN: new FormControl(false),
            StoreLocationNumber: new FormControl(false),
            AccountName: new FormControl(false),
            AccountNumber: new FormControl(false),
            ParentName: new FormControl(false),
            ParentSSN: new FormControl(false),
            ParentOrganization: new FormControl(false),
            ParentDepartment: new FormControl(false),
            ScholarshipEssay: new FormControl(false),
            ScholarshipEssay2: new FormControl(false)
        });

        this.scholarshipForm.get('AqYesRequired').disable();

        this.scholarshipForm.get('AqHaveYesNo').valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
            if (val === true) {
                this.scholarshipForm.get('AqYesRequired').enable();
            }
            else {
                this.scholarshipForm.get('AqYesRequired').disable();
            }
        });
    }

    getSchools() {
        this.appSvc
            .GetSchools()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.allSchools = res;
                    this.loading = false;
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    editSchools() {
        let dialog = this.matDialog.open<EditSchoolsModalComponent>(
            EditSchoolsModalComponent,
            {
                disableClose: true,
                data: {
                    selectedSchools: this.selectedSchools,
                    allSchools: this.allSchools,
                    scholarshipName: null,
                },
                width: '60%',
            }
        );

        dialog
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res: School[]) => {
                if (res == null) {
                    return;
                }

                this.selectedSchools = res;
            });
    }

    submit(form: FormGroup) {
        if (form.invalid) {
            this.formSubmitted = true;
            return;
        }

        let add: AddScholarship = form.value;
        add.SchoolList = this.selectedSchools;

        if (add.AdditionalQuestion != null && add.AdditionalQuestion.trim() != '') {
            if (add.AqHaveYesNo !== true && add.AqHaveText != true) {
                this.formSubmitted = true;
                this.aQInvalid = true;
                return;
            }
        }

        this.appSvc
            .AddScholarship(add)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Submit Successful', 5000);
                    this.router.navigate(['/admin-home']);
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }
}
